import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { API_BASE } from 'apiConfig';
import { setCustomerBlocked } from 'store/slices/trafficSlice';

interface Props {
  customerId?: string;
  /** Fallback blocked state if the visitor isn't in the live store. */
  blocked?: boolean;
  size?: 'sm' | 'lg';
  className?: string;
  /** Called after a successful block/unblock with the new state. */
  onToggled?: (blocked: boolean) => void;
}

/**
 * Block / unblock a visitor. A blocked visitor is refused the chat widget
 * (it removes itself) and any live session is kicked. Reads the live blocked
 * state from the store so it stays in sync across the app.
 */
const BlockCustomerButton = ({ customerId, blocked, size = 'sm', className, onToggled }: Props) => {
  const token = useSelector((s: RootState) => s.auth.token);
  const storeBlocked = useSelector(
    (s: RootState) => s.traffic.customers.find(c => c.customer_id === customerId)?.blocked
  );
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);

  if (!customerId) return null;
  const isBlocked = storeBlocked ?? blocked ?? false;

  const toggle = async () => {
    if (busy) return;
    if (
      !isBlocked &&
      !window.confirm(
        'Block this visitor? They will no longer be able to see or use the chat widget.'
      )
    ) {
      return;
    }
    const action = isBlocked ? 'unblock' : 'block';
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/customers/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ customer_id: customerId })
      });
      if (res.ok) {
        dispatch(setCustomerBlocked({ customer_id: customerId, blocked: !isBlocked }));
        onToggled?.(!isBlocked);
      } else {
        window.alert('Could not update block status. Please try again.');
      }
    } catch {
      window.alert('Could not update block status. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      variant={isBlocked ? 'outline-secondary' : 'outline-danger'}
      size={size}
      className={className}
      onClick={toggle}
      disabled={busy}
    >
      {busy ? '…' : isBlocked ? 'Unblock visitor' : 'Block visitor'}
    </Button>
  );
};

export default BlockCustomerButton;
